import { defineStore } from 'pinia'
import Locale from '@serv/Locale.js'
import Logging from '@serv/Logging'
import moment from 'moment'
import NotifyService from '@serv/NotifyService.js'
import Request from '@serv/Request'
import { type RtmDataCollectedType } from '@comp/popups/Popup98977RtmDataCollectionReview.vue'
import RtmEpisode from '@model/RtmEpisode'
import store from '@/store'
import useDownloadPdf from '@composables/useDownloadPdf'
import User from '@model/User'
import Utils from '@serv/Utils'
import { computed, ref } from 'vue'

export const useRtmEpisodesStore = defineStore('rtmEpisodes', () => {
    const patientRtmEpisodes = ref<RtmEpisode[]>([])
    const activeEpisode = computed(() => {
        return patientRtmEpisodes.value.find(episode => episode.withinSchedule)
    })

    const getPatientRtmEpisodes = (patientId: number): Promise<RtmEpisode[]> => {
        if (patientRtmEpisodes.value.length > 0) {
            return Promise.resolve(patientRtmEpisodes.value)
        }

        return Request.get(Request.Stem.patientRtmEpisodes, { params: { patientId } })
            .then(response => {
                if (response.data?.results) {
                    response.data.results.forEach((rtmEpisodeObj: RtmEpisode) => {
                        addRtmEpisode(rtmEpisodeObj)
                    })
                }

                return patientRtmEpisodes.value as RtmEpisode[]
            })
            .catch((error: Error) => {
                Logging.error(`Error fetching RTM episodes for patient: ${patientId}: ${error}`)

                return []
            })
    }

    const postPatientRtmEpisode = (patientId: number, payload: Partial<RtmEpisode>) => {
        return Request.post(Request.Stem.patientRtmEpisodes, payload, { params: { patientId } })
            .then(response => {
                if (response.data) {
                    addRtmEpisode(response.data)
                } else {
                    throw new Error(`No data returned from post after creating RTM episode, patientId: ${patientId}`)
                }
                NotifyService.success(Locale.getLanguageItem('rtmEnrolSuccess'))
            })
            .catch((error: Error) => {
                Logging.error(`Could not enrol patient ${patientId} into RTM: ${error}`)
                throw error
            })
    }

    const updateRtmEpisode = async (episodeId: number, payload: Partial<RtmEpisode>): Promise<RtmEpisode> => {
        const episode = patientRtmEpisodes.value.find(e => e.id === episodeId)
        try {
            if (!episode) {
                throw new Error(`No episode found with id ${episodeId}`)
            }

            const response = await Request.patch(Request.Stem.patientRtmEpisode, payload, {
                params: {
                    patientId: episode.patientId,
                    rtmEpisodeId: episodeId
                }
            })

            if (response.data) {
                const index = patientRtmEpisodes.value.findIndex(e => e.id === episodeId)
                if (index !== -1) {
                    patientRtmEpisodes.value[index] = new RtmEpisode(response.data)
                }

                return patientRtmEpisodes.value[index]
            }
            throw new Error('No data returned from update')
        } catch (error) {
            Logging.error(`Error updating RTM episode ${episodeId} for patient ${episode?.patientId}: ${error}`)
            throw error
        }
    }

    const addRtmEpisode = (rtmEpisodeObj: RtmEpisode) => {
        const rtmEpisode = new RtmEpisode(rtmEpisodeObj)

        patientRtmEpisodes.value.push(rtmEpisode)
    }

    const dischargePatientFromRtm = async (patientId: number): Promise<RtmEpisode[]> => {
        try {
            const response = await Request.post(
                Request.Stem.patientRtmDischarge,
                {},
                {
                    params: { patientId }
                }
            )

            if (response.data) {
                const index = patientRtmEpisodes.value.findIndex(e => e.id === response.data.id)
                if (index !== -1) {
                    patientRtmEpisodes.value[index] = new RtmEpisode(response.data)
                }
            }

            return patientRtmEpisodes.value
        } catch (error) {
            Logging.error(`Error discharging patient ${patientId} from RTM: ${error}`)
            throw error
        }
    }

    /* RTM capabilities and flags */
    const isActiveEpisodeRtmProvider = computed(() => {
        return activeEpisode.value?.rtmClinicianId === store.state.user?.user?.personaId
    })
    const isActiveEpisodeOwner = computed(() => {
        if (!activeEpisode.value) {
            return false
        }

        return activeEpisode.value.ownerSlug == store.state.user?.owner.slug
    })
    const isActiveEpisodeOwnerOrProvider = computed(() => {
        return activeEpisode.value && (isActiveEpisodeRtmProvider.value || isActiveEpisodeOwner.value)
    })
    const isActiveEpisodeEndingTomorrow = computed(() => {
        return (
            activeEpisode.value &&
            moment().add(1, 'days').startOf('day').isSame(moment(activeEpisode.value.endDate).startOf('day'))
        )
    })

    const ownerHasRtm = store.state.user.owner.hasFeatureFlag('hasRtm')
    const canReviewRtm = store.state.user.user.has(User.Capability.canReviewRtm)
    const canAdminRtm = store.state.user.user.has(User.Capability.canAdminRtm)
    const canViewRtmTab = ownerHasRtm && (canReviewRtm || canAdminRtm)
    const canDischargeFromRtm = computed(() => {
        return (
            activeEpisode.value &&
            canPerformRtmActions.value &&
            moment(activeEpisode.value.endDate).startOf('day').isAfter(moment().add(1, 'days').startOf('day'))
        )
    })
    const canDischargeReview = computed(() => {
        return (
            (canAdminRtm || canReviewRtm) &&
            isActiveEpisodeOwner.value &&
            store.state.user.owner.getTasksByType('rtmDischargeReview').length > 0
        )
    })
    const canDataCollectionReview = computed(() => {
        return (
            (canAdminRtm || canReviewRtm) &&
            isActiveEpisodeOwner.value &&
            store.state.user.owner.getTaskByType('rtmPeriodReview98977')
        )
    })
    const canExtendRtm = computed(() => {
        return activeEpisode.value && canPerformRtmActions.value && isActiveEpisodeEndingTomorrow.value
    })

    const canPerformRtmActions = computed(() => {
        return isActiveEpisodeOwner.value && (canReviewRtm || canAdminRtm)
    })

    /* Request Episode data collected */
    const getEpisodeDataCollected = ({
        patientId,
        episodeId,
        dataCollectedId
    }: {
        patientId: number
        episodeId: number
        dataCollectedId: number
    }): Promise<RtmDataCollectedType> => {
        const url = Request.Stem.patientRtmEpisodesDataCollected
            .replace('{patientId}', String(patientId))
            .replace('{episodeId}', String(episodeId))
            .replace('{dataCollectedId}', String(dataCollectedId))

        return Request.get<RtmDataCollectedType>(url)
            .then(response => {
                return response.data as RtmDataCollectedType
            })
            .catch(error => {
                Logging.error(
                    `Unable to load data collected for episode: episodeId: ${episodeId}, dataCollectedId: ${dataCollectedId}, patientId: ${patientId}`
                )

                throw error
            })
    }

    const getUserNameFromEpisode = (episode: RtmEpisode, idField: keyof RtmEpisode): string => {
        if (idField === 'rtmClinicianId' && !episode[idField]) {
            return Locale.getLanguageItem('rtmAddProvider')
        }

        if (!episode.ownerName) {
            Logging.error(`No ownerName for episode. episodeId: ${episode.id}, patientId: ${episode.patientId}`)
        }

        const userId = episode[idField] as number
        if (userId) {
            const users = store.state.user.users || {}

            return users[userId]?.titledFullName || episode.ownerName || ''
        }

        return episode.ownerName || ''
    }

    /* Download RTM summary PDF */
    const { downloadPdf } = useDownloadPdf()

    const downloadRtmSummaryPdf = (patientJourneyId: number, initials: string) => {
        const url = Request.Stem.rtmSummaryPdf.replace('{patientJourneyId}', String(patientJourneyId))
        const filename = `${initials} ${Locale.getLanguageItem('patientJourneyRtmSummary')} (${moment().format(Utils.readableDateFormat)})`
        downloadPdf(url, filename)
    }

    /* Clear everything in state */
    const $reset = () => {
        patientRtmEpisodes.value = []
    }

    return {
        $reset,
        activeEpisode,
        addRtmEpisode,
        canAdminRtm,
        canDataCollectionReview,
        canDischargeFromRtm,
        canDischargeReview,
        canExtendRtm,
        canReviewRtm,
        canPerformRtmActions,
        canViewRtmTab,
        dischargePatientFromRtm,
        getPatientRtmEpisodes,
        getUserNameFromEpisode,
        isActiveEpisodeOwner,
        isActiveEpisodeOwnerOrProvider,
        isActiveEpisodeRtmProvider,
        ownerHasRtm,
        patientRtmEpisodes,
        downloadRtmSummaryPdf,
        getEpisodeDataCollected,
        postPatientRtmEpisode,
        updateRtmEpisode
    }
})
